import Phaser from "phaser";
import { NineSlicePanel, type PanelStyleToken } from "@yjsgames/phaser-ui";

export interface CheatTextInputOptions {
  readonly width: number;
  readonly height?: number;
  readonly panelStyle: PanelStyleToken;
  readonly fontFamily: string;
  readonly fontSize: string;
  readonly textColor: string;
  readonly placeholderColor: string;
  readonly placeholder?: string;
}

export class CheatTextInput extends Phaser.GameObjects.Container {
  private readonly textObj: Phaser.GameObjects.Text;
  private readonly placeholder: string;
  private readonly textColor: string;
  private readonly placeholderColor: string;
  private readonly dom: HTMLInputElement;
  private value = "";

  constructor(scene: Phaser.Scene, x: number, y: number, opts: CheatTextInputOptions) {
    super(scene, x, y);
    const width = opts.width;
    const height = opts.height ?? 30;
    this.placeholder = opts.placeholder ?? "";
    this.textColor = opts.textColor;
    this.placeholderColor = opts.placeholderColor;

    const bg = new NineSlicePanel(scene, 0, 0, {
      style: opts.panelStyle,
      width,
      height,
      originX: 0.5,
      originY: 0.5,
    });
    this.add(bg);

    this.textObj = scene.add
      .text(-width / 2 + 8, 0, this.placeholder, {
        fontFamily: opts.fontFamily,
        fontSize: opts.fontSize,
        color: opts.placeholderColor,
      })
      .setOrigin(0, 0.5);
    this.add(this.textObj);

    const hit = scene.add
      .rectangle(0, 0, width, height)
      .setInteractive({ useHandCursor: true });
    hit.on("pointerdown", () => this.dom.focus());
    this.add(hit);

    this.dom = document.createElement("input");
    this.dom.type = "text";
    this.dom.autocomplete = "off";
    this.dom.style.cssText =
      "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;border:0;padding:0;";
    document.body.appendChild(this.dom);
    this.dom.addEventListener("input", this.onInput);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.removeDom);
    scene.events.once(Phaser.Scenes.Events.DESTROY, this.removeDom);
  }

  setValue(value: string): void {
    this.value = value;
    this.dom.value = value;
    this.refresh();
  }

  getValue(): string {
    return this.value;
  }

  override destroy(fromScene?: boolean): void {
    this.removeDom();
    super.destroy(fromScene);
  }

  private readonly onInput = (): void => {
    this.value = this.dom.value;
    this.refresh();
  };

  private refresh(): void {
    const has = this.value.length > 0;
    this.textObj.setText(has ? this.value : this.placeholder);
    this.textObj.setColor(has ? this.textColor : this.placeholderColor);
  }

  private readonly removeDom = (): void => {
    this.dom.removeEventListener("input", this.onInput);
    this.dom.remove();
  };
}
